import { Box } from '@mui/material';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { EditableSectionContainer, EditableSectionRow, useEditableSectionsContext } from '../../../EditableSection';
import { Formik, FormikProps } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { InsuranceCardForm, InsuranceDetailsForm, InsuranceFormType, PrimarySubscriberFormBlock, shouldCheckIfIsPrimaryInsurance, useInsuranceForm } from '../forms';
import { AddInsuranceStepper } from '../Insurances/add-insurance-stepper.component';
import { AddInsuranceSection, AddInsuranceView, InsuranceSection } from '../Settings/types';
import { IsPrimaryInsuranceForm } from './is-primary-insurance-form.form';
import { useInsuranceCompaniesQuery } from '@uplift/queries';
import { useInsurances } from '../hooks/use-insurance.hook';
import { AddInsuranceMobileFormWrapper } from '../Insurances/add-insurance-mobile-form-wrapper.component';
import { AddInsuranceActionsBar } from '../Insurances/add-insurance-actions-bar.component';


export type AddInsuranceSectionItemProps = {
  addInsuranceSection: AddInsuranceSection;
  onCancel: () => void;
  patientId: string;
};


export const AddInsuranceSectionItem: React.VFC<AddInsuranceSectionItemProps> = ({ addInsuranceSection, onCancel, patientId }) => {
  const l10n = useL10n();
  const isMobile = useIsMobile('sm');
  const insuranceSection = [ AddInsuranceSection.addPrimaryInsurance, AddInsuranceSection.replacePrimaryInsurance ].includes(addInsuranceSection)
    ? InsuranceSection.primaryInsurance
    : InsuranceSection.secondaryInsurance;
  const { insuranceFormik, ...validations } = useInsuranceForm(patientId, insuranceSection, true);
  const [ currentStep, setCurrentStep ] = useState<AddInsuranceView>(AddInsuranceView.details);
  const { data: insurancesData } = useInsurances(patientId);
  const { data: insuranceCompanies } = useInsuranceCompaniesQuery();

  const getActions = useCallback((formik: FormikProps<InsuranceFormType>) => {
    const shouldCheckIfIsPrimary = shouldCheckIfIsPrimaryInsurance({
      primaryInsuranceCompanyId: insurancesData?.[InsuranceSection.primaryInsurance]?.companyId,
      currentInsuranceCompanyId: formik.values.insuranceProvider,
      insuranceCompanies: insuranceCompanies ?? [],
      addInsuranceSection
    });

    switch (currentStep) {
      case AddInsuranceView.details:
        return {
          cancel: onCancel,
          next: () => setCurrentStep(shouldCheckIfIsPrimary ? AddInsuranceView.detailsIsPrimaryInsurance : AddInsuranceView.card),
          nextDisabled: !validations.isInsuranceDetailsValid(formik.values)
        };
      case AddInsuranceView.detailsIsPrimaryInsurance:
        return {
          cancel: onCancel,
          back: () => setCurrentStep(AddInsuranceView.details),
          next: () => setCurrentStep(AddInsuranceView.card),
          nextDisabled: !validations.isInsuranceDetailsIsPrimaryInsuranceValid(formik.values)
        };
      case AddInsuranceView.card:
        return {
          cancel: onCancel,
          back: () => setCurrentStep(shouldCheckIfIsPrimary ? AddInsuranceView.detailsIsPrimaryInsurance : AddInsuranceView.details),
          next: () => setCurrentStep(AddInsuranceView.primarySubscriber),
          nextDisabled: !validations.isInsuranceCardsValid(formik.values)
        };
      case AddInsuranceView.primarySubscriber:
        return {
          cancel: onCancel,
          back: () => setCurrentStep(AddInsuranceView.card),
          next: formik.handleSubmit,
          nextLabel: l10n.accountSettings.addInsurance.addInsurance,
          nextDisabled: !validations.isPrimarySubscriberValid(formik.values),
          loading: formik.isSubmitting,
        };
    }
  }, [ addInsuranceSection, currentStep, insuranceCompanies, insurancesData, l10n.accountSettings.addInsurance.addInsurance, onCancel, validations ]);

  const form = useMemo(() => {
    return {
      details: <InsuranceDetailsForm isAddInsurance insuranceSection={insuranceSection} patientId={patientId} />,
      detailsIsPrimaryInsurance: <IsPrimaryInsuranceForm patientId={patientId} />,
      card: <InsuranceCardForm gap="16px" dropzoneHeight={246} isMobile={isMobile} dropzoneColor="secondary" />,
      primarySubscriber: <PrimarySubscriberFormBlock patientId={patientId} isMobile={isMobile} />
    }[currentStep];
  }, [ currentStep, insuranceSection, isMobile, patientId ]);

  const steps = [
    { label: l10n.accountSettings.addInsurance.insuranceDetails, value: AddInsuranceView.details },
    { label: l10n.accountSettings.addInsurance.insuranceDetails, value: AddInsuranceView.detailsIsPrimaryInsurance },
    { label: l10n.accountSettings.addInsurance.insuranceCard, value: AddInsuranceView.card },
    { label: l10n.accountSettings.insurance.primarySubscriber, value: AddInsuranceView.primarySubscriber },
  ];

  const stepper = (
    <AddInsuranceStepper
      steps={steps.filter(step => step.value !== AddInsuranceView.detailsIsPrimaryInsurance)}
      currentStep={currentStep === AddInsuranceView.detailsIsPrimaryInsurance ? AddInsuranceView.details : currentStep}
      isMobile={isMobile}
    />
  );

  return (
    <Formik {...insuranceFormik()}>
      {(formik) => {
        const actions = getActions(formik);

        if (isMobile) {
          const title = steps.find(({ value }) => value === currentStep)?.label;

          return (
            <AddInsuranceMobileFormWrapper title={`Add ${title}`} {...actions}>
              <Box display="flex" flexDirection="column" gap="2rem">
                <div>
                  {stepper}
                </div>
                <div>
                  {form}
                </div>
              </Box>
            </AddInsuranceMobileFormWrapper>
          );
        }

        return (
          <EditableSectionContainer
            title={l10n.accountSettings.addInsurance.addInsurance}
            rowDividers
          >
            <EditableSectionRow
              kind="custom"
              rowKey="stepper"
            >
              {stepper}
            </EditableSectionRow>
            <EditableSectionRow
              kind="custom"
              rowKey="form"
            >
              {form}
            </EditableSectionRow>
            <EditableSectionRow
              kind="custom"
              rowKey="actions"
            >
              <AddInsuranceActionsBar {...actions} />
            </EditableSectionRow>
          </EditableSectionContainer>
        );
      }}
    </Formik>
  );
};

export type AddInsuranceProps = {
  children: React.ReactNode;
  addInsuranceSection: AddInsuranceSection | AddInsuranceSection[] | null;
  patientId: string;
};

export const AddInsurance: React.FC<AddInsuranceProps> = ({ children, addInsuranceSection, patientId }) => {
  const { editKey, setEditKey } = useEditableSectionsContext();
  const isMobile = useIsMobile('sm');

  const isEditRow = Array.isArray(addInsuranceSection) ? addInsuranceSection.includes(editKey as AddInsuranceSection) : addInsuranceSection === editKey;
  const insuranceSection = Array.isArray(addInsuranceSection) ? addInsuranceSection.find((key) => key === editKey) : addInsuranceSection;

  if (!isEditRow || !insuranceSection) {

    return <>{children}</>;
  }

  const section = <AddInsuranceSectionItem patientId={patientId} onCancel={() => setEditKey(null)} addInsuranceSection={insuranceSection} />;

  if (isMobile) {
    return (
      <>
        {children}
        {section}
      </>
    );
  }

  return section;
};
